import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    OutlinedInput,
    TextField,
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import ReactPasswordChecklist from "react-password-checklist";
import {useFetch} from "../../hooks/useFetch";
import {IRole} from "../../interfaces/IRole";
import {ISubCalendar} from "../../interfaces/ISubCalendar";
import {IProfile, IUser, RelationshipDirection, RelationshipType} from "../../interfaces/IUser";
import {CustomeTextField} from "../core/CustomeTextField";
import ProfileAussendienstAssignment from "./ProfileAussendienstAssignment";
import ProfileInnendienstAssignment, {
    isAussendienstWorker,
    isInnendienstWorker,
} from "./ProfileInnendienstAssignment";
import {ProfilePlzAssignment} from "./ProfilePlzAssignment";
import CheckIcon from "@mui/icons-material/Check";
import {ListItemIcon, ListItemText} from "@mui/material";


interface IProps {
    workerObject: IProfile;
    otherWorkers: IProfile[];
    roleArray: IRole[];
    setWorkerObject: Function;
    setUserPassword: Function;
}


function isAssignedToAussendienst(workerObject: IProfile) {
    return workerObject.relationships.some(
        (rel) => rel.type === RelationshipType.Innendienst && rel.direction === RelationshipDirection.Outgoing,
    );
}

function hasAssignedInnendienst(workerObject: IProfile) {
    return workerObject.relationships.some(
        (rel) => rel.type === RelationshipType.Innendienst && rel.direction === RelationshipDirection.Incoming,
    );
}

export const ProfileEdit: React.FC<IProps> = ({
                                                  workerObject,
                                                  roleArray,
                                                  setWorkerObject,
                                                  setUserPassword,
                                                  otherWorkers,
                                              }) => {
    const is_superuser = localStorage.getItem("is_superuser") === "true";

    const [userObject, setUserObject] = useState<IUser>({
        ...workerObject.User,
    });
    const [disableUserName, setDisableUsername] = useState(false);
    //const [permissionArray,setPermissionArray] = useState(workerObject.permissions);

    const [showPW, setshowPW] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordAgain, setNewPasswordAgain] = useState("");
    const [isValid, setIsValid] = useState(false);

    const [roleRelationsWarning, setRoleRelationsWarning] = useState(false);

    const [bufferedWorkerObject, setBufferedWorkerObject] = useState<IProfile | null>(null);

    // Verfügbare SubCalendars aus dem Endpoint "/calendar/sub_calendar/" laden.
    const [availableSubCalendars] = useFetch<ISubCalendar[]>("/calendar/sub_calendar/");
    const [subCalSelectOpen, setSubCalSelectOpen] = useState(false);


    const handleSubCalendarChange = (event: SelectChangeEvent<string[]>) => {
        const {value} = event.target;
        const valueArray = typeof value === "string" ? value.split(",") : value;
        setWorkerObject({
            ...workerObject,
            sub_calendars: valueArray.map((val) => Number(val)),
        });
    };

    useEffect(() => {
        if (isValid) {
            setWorkerObject({
                ...workerObject,
                User: {
                    ...userObject,
                    password: newPassword,
                },
            });
        } else {
            setWorkerObject({
                ...workerObject,
                User: userObject,
            });
        }
    }, [isValid, userObject]);

    const availableInnendienstWorkers = useMemo(
        () =>
            otherWorkers.filter(
                (worker) =>
                    worker.id !== workerObject.id &&
                    isInnendienstWorker(worker) &&
                    !worker.relationships.find(
                        (rel) =>
                            rel.type === RelationshipType.Innendienst &&
                            rel.direction === RelationshipDirection.Incoming,
                    ),
            ),
        [otherWorkers, workerObject.id],
    );
    const availableAussendienstWorkers = useMemo(
        () =>
            otherWorkers.filter(
                (worker) =>
                    worker.id !== workerObject.id &&
                    isAussendienstWorker(worker) &&
                    !worker.relationships.find(
                        (rel) =>
                            rel.type === RelationshipType.Innendienst &&
                            rel.direction === RelationshipDirection.Incoming &&
                            rel.peer !== workerObject.id,
                    ),
            ),
        [otherWorkers, workerObject.id],
    );
    const assignedAussendienstWorkers = useMemo(
        () =>
            otherWorkers.filter((w) =>
                workerObject.relationships.find(
                    (rel) =>
                        rel.type === RelationshipType.Innendienst &&
                        rel.direction === RelationshipDirection.Outgoing &&
                        rel.peer === w.id,
                ),
            ),
        [otherWorkers, workerObject.relationships],
    );

    if (workerObject.id > 0 && workerObject === undefined && availableSubCalendars === undefined) {
        return <CircularProgress/>;
    } else {
        return (
            <>
                <Collapse in={showPW && !isValid}>
                    <Alert severity="warning">
                        Bitte geben Sie ein valdies Passwort ein. Andernfalls wird das neue Passwort nicht übernommen!
                    </Alert>
                </Collapse>
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item sm={4}>
                        <CustomeTextField
                            label="Admin"
                            attr="is_superuser"
                            type="boolean"
                            object={userObject}
                            setObject={setUserObject}
                            disabled={!is_superuser}
                        />
                    </Grid>
                    <Grid item sm={8}>
                        <CustomeTextField
                            label="Benutzername"
                            attr="username"
                            type="string"
                            object={userObject}
                            setObject={setUserObject}
                            disabled={disableUserName}
                            required
                        />
                    </Grid>

                    <Grid item sm={12}>
                        <Box sx={{mt: 4}}/>
                        <CustomeTextField
                            label="Rolle"
                            attr="Role"
                            type="select"
                            object={workerObject}
                            setObject={(changedWorkerObject: IProfile) => {
                                if (
                                    (isInnendienstWorker(workerObject) &&
                                        isAssignedToAussendienst(workerObject) &&
                                        !isInnendienstWorker(changedWorkerObject)) ||
                                    (isAussendienstWorker(workerObject) &&
                                        hasAssignedInnendienst(workerObject) &&
                                        !isAussendienstWorker(changedWorkerObject))
                                ) {
                                    setRoleRelationsWarning(true);
                                    setBufferedWorkerObject(changedWorkerObject);
                                    return;
                                }
                                setWorkerObject(changedWorkerObject);
                            }}
                            disabled={disableUserName}
                            required
                        >
                            {roleArray?.map((role) => (
                                <MenuItem key={`role-${role.id}`} value={role.id}>
                                    {role.role}
                                </MenuItem>
                            ))}
                        </CustomeTextField>
                        <Box sx={{mb: 4}}/>
                    </Grid>

                    <Dialog
                        open={roleRelationsWarning}
                        onClose={() => {
                            setBufferedWorkerObject(null);
                            setRoleRelationsWarning(false);
                        }}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>Rollenwechsel</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                <p>
                                    {isInnendienstWorker(workerObject)
                                        ? "Dieser Benutzer wurde mindestens einem Aussendienstmitarbeiter zugeordnet."
                                        : isAussendienstWorker(workerObject)
                                            ? "Diesem Benutzer wurde ein Innendienstmitarbeiter zugeordnet."
                                            : ""}{" "}
                                </p>
                                <p>Die Zuordnung wird gelöscht, wenn Sie die Rolle ändern. Möchten Sie fortfahren?</p>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setBufferedWorkerObject(null);
                                    setRoleRelationsWarning(false);
                                }}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                onClick={() => {
                                    setWorkerObject({
                                        ...bufferedWorkerObject,
                                        relationships: [],
                                    });
                                    setBufferedWorkerObject(null);
                                    setRoleRelationsWarning(false);
                                }}
                                autoFocus
                                variant="contained"
                            >
                                Bestätigen
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Grid item sm={6}>
                        <CustomeTextField
                            label="Vorname"
                            attr="first_name"
                            type="string"
                            object={userObject}
                            setObject={setUserObject}
                            required
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <CustomeTextField
                            label="Nachname"
                            attr="last_name"
                            type="string"
                            object={userObject}
                            setObject={setUserObject}
                        />
                    </Grid>
                    <Grid item sm={12}>
                        <CustomeTextField
                            label="Email"
                            attr="email"
                            type="string"
                            object={userObject}
                            setObject={setUserObject}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <CustomeTextField
                            label="Mobil"
                            attr="mobile_number"
                            type="string"
                            object={workerObject}
                            setObject={setWorkerObject}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <CustomeTextField
                            label="Telefon"
                            attr="phone_number"
                            type="string"
                            object={workerObject}
                            setObject={setWorkerObject}
                        />
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="sub-calendar-label">zusätzliche Kalender</InputLabel>
                            <Select
                                labelId="sub-calendar-label"
                                multiple
                                open={subCalSelectOpen}
                                onOpen={() => setSubCalSelectOpen(true)}
                                onClose={() => setSubCalSelectOpen(false)}

                                value={workerObject.sub_calendars?.map(String) || []}
                                renderValue={(selected) => {
                                    const selectedIds = selected as string[];
                                    const selectedNames = availableSubCalendars
                                        ?.filter((cal) => selectedIds.includes(cal.id.toString()))
                                        .map((cal) => cal.name) || [];
                                    return selectedNames.join(", ");
                                }}
                                onChange={(e) => {
                                    handleSubCalendarChange(e);
                                    setSubCalSelectOpen(false);
                                }}
                                input={<OutlinedInput label="zusätzliche Kalender"/>}
                            >
                                {availableSubCalendars?.map((cal) => {
                                    const isSelected = workerObject.sub_calendars?.includes(cal.id);
                                    return (
                                        <MenuItem
                                            key={cal.id}
                                            value={cal.id.toString()}
                                            selected={isSelected}
                                            sx={{
                                                "&.Mui-selected": {
                                                    backgroundColor: "rgba(76,175,80,0.2)",
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 32,
                                                    color: isSelected ? "success.main" : "transparent",
                                                }}
                                            >
                                                <CheckIcon fontSize="small"/>
                                            </ListItemIcon>
                                            <ListItemText primary={cal.name}/>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>


                </Grid>
                <Box sx={{mt: 4}}/>
                <ProfilePlzAssignment
                    assignedPlzs={workerObject.assigned_postcodes}
                    setAssignedPlzs={(plz) => {
                        setWorkerObject({
                            ...workerObject,
                            assigned_postcodes: plz,
                        });
                    }}
                />
                {isInnendienstWorker(workerObject) && (
                    <>
                        <Box sx={{mt: 4}}/>
                        <ProfileAussendienstAssignment
                            availableAussendienstWorkers={availableAussendienstWorkers}
                            assignedAussendienstWorkers={assignedAussendienstWorkers}
                            handleAssignmentConfirmation={(selectedWorkerIds) => {
                                setWorkerObject({
                                    ...workerObject,
                                    relationships: [
                                        ...workerObject.relationships.filter(
                                            (rel) =>
                                                !selectedWorkerIds.includes(rel.peer as number) &&
                                                rel.type !== RelationshipType.Innendienst &&
                                                rel.direction !== RelationshipDirection.Outgoing,
                                        ),
                                        ...selectedWorkerIds.map((id) => ({
                                            peer: id,
                                            direction: RelationshipDirection.Outgoing,
                                            type: RelationshipType.Innendienst,
                                        })),
                                    ],
                                });
                            }}
                        />
                    </>
                )}
                {isAussendienstWorker(workerObject) && (
                    <>
                        <Box sx={{mt: 4}}/>
                        <ProfileInnendienstAssignment
                            worker={workerObject}
                            availableInnendienstEmployees={availableInnendienstWorkers}
                            updateInnendienstAssignment={(innendienstAssignmentProfileId: number | null) => {
                                setWorkerObject({
                                    ...workerObject,
                                    relationships: [
                                        ...workerObject.relationships.filter(
                                            (rel) =>
                                                rel.type !== RelationshipType.Innendienst &&
                                                rel.direction !== RelationshipDirection.Incoming,
                                        ),
                                        innendienstAssignmentProfileId && {
                                            peer: innendienstAssignmentProfileId,
                                            direction: RelationshipDirection.Incoming,
                                            type: RelationshipType.Innendienst,
                                        },
                                    ].filter(Boolean),
                                });
                            }}
                        />
                    </>
                )}
                <Box sx={{mt: 5}}/>
                <Collapse in={!showPW}>
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <TextField
                                label="Passwort"
                                value="Geh****"
                                size="small"
                                fullWidth
                                onFocus={() => setshowPW(true)}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
                <Collapse in={showPW}>
                    <Grid container spacing={2}>
                        <Grid item sm={6}>
                            <TextField
                                type="password"
                                label="Passwort"
                                value={newPassword}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setNewPassword(event.target.value);
                                }}
                                fullWidth
                                size="small"
                                onBlur={() => setshowPW(newPassword !== "")}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <TextField
                                type="password"
                                label="Passwort (wiederholen)"
                                value={newPasswordAgain}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setNewPasswordAgain(event.target.value);
                                }}
                                fullWidth
                                size="small"
                                onBlur={() => setshowPW(newPassword !== "")}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <ReactPasswordChecklist
                                rules={["minLength", "specialChar", "number", "capital", "match"]}
                                minLength={8}
                                value={newPassword}
                                valueAgain={newPasswordAgain}
                                onChange={(isValid) => setIsValid(isValid)}
                                messages={{
                                    minLength: "Das Passwort hat 8 Zeichen.",
                                    specialChar: "Das Passwort hat ein Sonderzeichen.",
                                    number: "Das Passwort hat eine Zahl.",
                                    capital: "Das Passwort hat ein Großbuchstaben.",
                                    match: "Die Passwörter stimmen überein",
                                }}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
            </>
        );
    }
};
